/**
 * Four pillar cards covering the moats the v1.13 era has stacked.
 *
 * Order matters: pre-signature enforcement leads because it's the
 * most defensible moat (no non-custodial agent harness has policy-
 * driven pre-signature on the user's machine). Non-custodial is
 * 02 because it's table stakes for self-custody users but Fireblocks/
 * Fordefi also have custodial pre-signature — the unique stack is
 * the combination, which is what 01 + 02 read as together.
 *
 * 04 is the v1.11 wake-up surface — net-new in this session, fully
 * unique vs every other tool in the agent-trading space.
 */
export function Pillars() {
  return (
    <section id="product" className="ax-section">
      <div className="ax-section-head">
        <div className="ax-eyebrow">01 · Why Axon</div>
        <h2 className="ax-h2">
          One skill.<br />
          <span style={{ color: "var(--at-fg-dim)" }}>Four guarantees.</span>
        </h2>
      </div>

      <div className="ax-pillars">
        <Pillar
          num="01"
          title="Bad trades blocked, not alerted"
          body="RiskGuard validates every agent intent before it signs. Forbidden intents get a typed rejection code the agent can reason about."
          detail="pre-signature · typed rejection codes · kill switch"
        />
        <Pillar
          num="02"
          title="Non-custodial by construction"
          body="Owner key encrypted on your machine. Daemon signs locally. Agent never sees the key. Operator liability: zero."
          detail="scrypt N=2¹⁷ · AES-256-GCM · mode 0600"
        />
        <Pillar
          num="03"
          title="Any agent, any runtime"
          body="One command wires 30+ typed MCP tools into Claude Code, Cursor, Codex, Hermes, and 4 more. No new platform, no new wallet, no new identity."
          detail="8 runtimes · 1 install · zero JSON config"
        />
        <Pillar
          num="04"
          title="Wake up to what you missed"
          body="Typed alerts emit while idle — kill switch, SL/TP, health factor, strategy errors. Agent reads them first thing on its next turn."
          detail="get_pending_alerts · severity-graded · suggestedActions"
        />
      </div>

      <style>{`
        .ax-pillars {
          display: grid; grid-template-columns: repeat(4, 1fr);
          gap: 14px;
        }
        @media (max-width: 1200px) {
          .ax-pillars { grid-template-columns: repeat(2, 1fr); gap: 16px; }
        }
        @media (max-width: 700px) {
          .ax-pillars { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
}

function Pillar({
  num, title, body, detail,
}: { num: string; title: string; body: string; detail: string }) {
  return (
    <article className="ax-pillar">
      <div className="ax-pillar-num">{num}</div>
      <h3 className="ax-h3">{title}</h3>
      <p className="ax-pillar-body">{body}</p>
      <div className="ax-pillar-detail">{detail}</div>
      <style>{`
        .ax-pillar {
          background: var(--at-surface);
          border: 1px solid var(--at-hairline);
          border-radius: 14px;
          padding: 28px 26px;
          display: flex; flex-direction: column;
          transition: border-color var(--at-dur-fast) var(--at-ease),
                      transform var(--at-dur-fast) var(--at-ease);
        }
        .ax-pillar:hover {
          border-color: rgba(74, 222, 128, 0.28);
          transform: translateY(-2px);
        }
        .ax-pillar-num {
          font-family: var(--at-font-mono);
          font-size: 11px; letter-spacing: 0.3em;
          color: var(--at-accent);
          margin-bottom: 24px;
        }
        .ax-pillar h3 {
          font-size: 20px; font-weight: 600;
          letter-spacing: -0.01em;
          color: var(--at-fg);
          margin: 0 0 14px;
          line-height: 1.25;
        }
        .ax-pillar-body {
          font-size: 14.5px; line-height: 1.6;
          color: var(--at-fg-dim);
          margin: 0 0 24px;
          flex: 1;
        }
        .ax-pillar-detail {
          font-family: var(--at-font-mono);
          font-size: 11px;
          color: var(--at-muted);
          padding-top: 16px;
          border-top: 1px solid var(--at-hairline);
          letter-spacing: 0.02em;
        }
      `}</style>
    </article>
  );
}
